import { Injectable } from '@angular/core';
import { AsyncValidatorFn, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

import { Observable, of } from 'rxjs';
import { delay, switchMap, map, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { RestService } from '../services/rest.service';
import { environment } from 'src/environments/environment';
import { number } from 'echarts';
import { Utils } from '../utils';

/** Valid when the national number has the length of the selected phone country (see Utils.phoneCountries). */
export function phoneDigitsValidator(getCountryCode: () => string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const digits = (control.value ?? '').toString().replace(/\D/g, '');
    if (!digits) { return null; } // required is handled by Validators.required
    return digits.length === Utils.getPhoneCountry(getCountryCode()).length ? null : { phoneDigits: true };
  };
}

@Injectable({
  providedIn: 'root',
})
export class InfinityValidators {
  constructor(
    private restService: RestService,
    private _purchaseOrderService: RestService
  ) { }

  public asyncUsernameTakenByEmployeeValidator(id: number=0): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return of(control.value).pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((userName: string) =>
          // userName, id
          this.restService.get(`${environment.urls.Employee_username_taken}?userName=${userName}&id=${id}`,true).pipe(
            map(res => {
              
              return res.result === true ? { userNameTaken: true } : null;
            })
          )
        )
      );
    };
  }
  public asyncTenantEmailUniqueValidator(id: number = 0): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return of(control.value).pipe(
        debounceTime(100),
        distinctUntilChanged(),
        switchMap((email: string) => {
          if (!email) return of(null);

          // ✅ no "true" here — default isLocal = false (uses api/services/app)
          return this.restService
            .get(`${environment.urls.Tenant_IsEmailUnique}?email=${encodeURIComponent(email)}&id=${id}`)
            .pipe(
              map((res: any) => {
                // assuming backend returns { result: true } if unique
                return res.result === false ? { emailExists: true } : null;
              })
            );
        })
      );
    };
  }
public asyncTenantCompanyNameUniqueValidator(): AsyncValidatorFn {
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    return of(control.value).pipe(
      debounceTime(100),
      distinctUntilChanged(),
      switchMap((companyName: string) => {
        if (!companyName) return of(null);

        const url = `${environment.urls.Tenant_IsCompanyNameUnique}?companyName=${encodeURIComponent(companyName)}`;

        return this.restService.get(url).pipe(
          map((res: any) => {
            // assuming backend returns { result: true } if unique
            return res.result === false ? { companyNameExists: true } : null;
          })
        );
      })
    );
  };
}
/** Tenancy name must be unused by tenants and prospects; the record's own saved value is skipped. */
public asyncTenancyNameAvailableValidator(original: () => string = () => ''): AsyncValidatorFn {
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    return of(control.value).pipe(
      debounceTime(100),
      distinctUntilChanged(),
      switchMap((tenancyName: string) => {
        if (!tenancyName || tenancyName.toLowerCase() === original()?.toLowerCase()) return of(null);
        return this.restService.get(`${environment.urls.Tenant_IsTenancyNameUnique}?tenancyName=${encodeURIComponent(tenancyName)}`).pipe(
          map((res: any) => res.result === false ? { tenancyNameExists: true } : null)
        );
      })
    );
  };
}


  // public asyncUsernameTakenByUserValidator(id?: any): AsyncValidatorFn {
  //   return (control: AbstractControl): Observable<ValidationErrors | null> => {
  //     return of(control.value).pipe(
  //       debounceTime(400),
  //       distinctUntilChanged(),
  //       switchMap((userName: string) =>
  //         this._employeeService.userNameTakenByUserByUserNameAndUserId(userName, id).pipe(
  //           map(res => {
  //             return res === true ? { userNameTaken: true } : null;
  //           })
  //         )
  //       )
  //     );
  //   };
  // }

  public asyncEmailAddressTakenByEmployeeValidator(id?: number): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return of(control.value).pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((email: string, id:number) =>
          this.restService.get(`${environment.urls.Employee_email_taken_By_EmployeeId}?email=${email}&id=${id}`,true).pipe(
            map(res => {
              return res.result === true ? { emailTaken: true } : null;
            })
          )
        )
      );
    };
  }
  public asyncInvoiceNumberValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return of(control.value).pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((invoiceId?: number) => {
          if (invoiceId) {
            return this._purchaseOrderService.post(`${environment.urls.PurchaseOrder_IsInvoiceNumberValidt}?invoiceId=${invoiceId}`,{}).pipe(
              map(res => {
                return res.result === false ? { invalidInvoiceNumber: true } : null;
              })
            );
          } else {
            return of(null);
          }
        })
      );
    };
  }

  public patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }

      const valid = regex.test(control.value);

      return valid ? null : error;
    };
  }
}
